/* eslint-disable jsx-a11y/label-has-associated-control */
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Button, Dialog, DialogContent, DialogTitle, Paper, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Document, Page } from 'react-pdf';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { splitPdf } from '../../../api/invoicing';
import { getInvoiceBreakdown, getInvoiceFiles, getInvoicesInParsing, renderInvoiceFile } from '../../../redux/slices/invoicing';
import { useDispatch } from '../../../redux/store';
import palette from '../../../theme/palette';
import { AwaitButton } from '../../await-button';
import CloseIconButton from '../../basic-buttons/CloseIconButton';
import { useSnackbar } from '../../snackbar';

SplitPDF.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  blobUrl: PropTypes.string
}

export default function SplitPDF({ blobUrl, open, setOpen }) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(blobUrl);
  const [numPages, setNumPages] = useState(null);
  const [cutLines, setCutLines] = useState([]);
  const [excludedPages, setExcludedPages] = useState([]);

  const { id } = useParams();

  useEffect(() => {
    if (!blobUrl || !open) return; // don't fetch if blobUrl is not ready or the dialog is closed
    fetch(blobUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const newFile = new File([blob], 'fileName.pdf', { type: blob.type });
        setPdfFile(newFile);
      });
  }, [blobUrl, open]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openDropzone,
  } = useDropzone({
    accept: {
      'application/pdf': [],
    },
    onDrop: (acceptedFiles) => {
      setPdfFile(acceptedFiles[0]);
    },
  });

  const handleClose = () => {
    setPdfFile(null);
    setCutLines([]);
    setExcludedPages([]);
    setOpen(false);
  };

  const split = async () => {
    if (numPages && excludedPages.length >= numPages) {
      return;
    }

    const validCutLines = cutLines.filter((line) => isAvailableCutLine(line));
    const response = await splitPdf(validCutLines, excludedPages, pdfFile, id);
    if (response.status === 200) {
      handleClose();
      await dispatch(renderInvoiceFile(id, true));
      await dispatch(getInvoiceBreakdown(id, 'ItemsLog'));
      await dispatch(getInvoicesInParsing());
    } else {
      enqueueSnackbar('Error splitting pdf', { variant: 'error' });
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    setPdfFile(file);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const isAvailableCutLine = (line) => {
    if (!numPages || line <= 0 || line >= numPages) {
      return false;
    }

    const hasPageBefore = Array.from({ length: line }, (_, idx) => idx + 1)
      .some((pageNum) => !excludedPages.includes(pageNum));
    const hasPageAfter = Array.from({ length: numPages - line }, (_, idx) => line + idx + 1)
      .some((pageNum) => !excludedPages.includes(pageNum));

    return hasPageBefore && hasPageAfter;
  };

  useEffect(() => {
    setCutLines((prevLines) => prevLines.filter((line) => isAvailableCutLine(line)));
  }, [excludedPages, numPages]);

  const toggleCutLine = (pageNumber) => {
    setCutLines((prevLines) =>
      prevLines.includes(pageNumber) ? prevLines.filter((page) => page !== pageNumber) : [...prevLines, pageNumber]
    );
  };

  const toggleDeletedPage = (pageNumber) => {
    setExcludedPages((prevPages) => {
      const isCurrentlyDeleted = prevPages.includes(pageNumber);
      if (!isCurrentlyDeleted) {
        // If a cut line was above this page, move it to after this page when possible.
        setCutLines((prevLines) => {
          const cutLineAbovePage = pageNumber - 1;
          if (!prevLines.includes(cutLineAbovePage)) {
            return prevLines;
          }

          const nextCutLine = pageNumber;
          const movedLines = prevLines.filter((line) => line !== cutLineAbovePage);

          if (numPages && pageNumber < numPages && !movedLines.includes(nextCutLine)) {
            return [...movedLines, nextCutLine];
          }

          return movedLines;
        });
      }

      return isCurrentlyDeleted
        ? prevPages.filter((page) => page !== pageNumber)
        : [...prevPages, pageNumber];
    });
  };

  const renderCutLines = (pageNumber) => (
    pageNumber !== 0 &&
    <div
      style={{
        position: 'absolute',
        top: -8,
        left: 0,
        width: '100%',
        borderWidth: '1px',
        borderStyle: 'dashed',
        color: cutLines.includes(pageNumber) && isAvailableCutLine(pageNumber) ? palette.info.main : '#575757',
      }}
    />
  );

  const renderButton = (pageNumber) => (
    <Box sx={{ position: 'absolute', top: 0, left: 0, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
      {pageNumber !== 0 && !excludedPages.includes(pageNumber + 1) && isAvailableCutLine(pageNumber) && (
        <Button
          onClick={() => toggleCutLine(pageNumber)}
          startIcon={cutLines.includes(pageNumber) ? <UndoIcon /> : <ContentCutIcon />}
          sx={{
            color: cutLines.includes(pageNumber) ? 'info.main' : 'secondary.main',
          }}
        >
          {cutLines.includes(pageNumber) ? 'Undo Cut Line' : 'Add Cut Line'}
        </Button>
      )}
      <Button
        onClick={() => toggleDeletedPage(pageNumber + 1)}
        startIcon={excludedPages.includes(pageNumber + 1) ? <UndoIcon /> : <DeleteOutlineIcon />}
        color={excludedPages.includes(pageNumber + 1) ? 'warning' : 'error'}
      >
        {excludedPages.includes(pageNumber + 1) ? 'Restore Page' : 'Delete Page'}
      </Button>
    </Box>
  );

  const renderPages = () => {
    const pages = [];
    for (let i = 0; i < numPages; i++) {
      pages.push(
        <Box
          key={i}
          sx={{
            mb: 2,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            '& .react-pdf__Page__annotations': {
              display: 'none',
            },
          }}
        >
          <Page pageNumber={i + 1} renderTextLayer={false} />
          {excludedPages.includes(i + 1) && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(140, 0, 0, 0.28)',
                border: 1,
                borderColor: 'error.main',
                pointerEvents: 'none',
              }}
            />
          )}
          {renderCutLines(i)}
          {renderButton(i)}
        </Box>
      );
    }
    return pages;
  };


  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{ textAlign: 'center' }}>
          Split PDF
          <CloseIconButton onClick={handleClose}/>
          {(cutLines.length > 0 || excludedPages.length > 0) && (!numPages || excludedPages.length < numPages) && (
            <AwaitButton
              variant="outlined"
              color="info"
              sx={{
                position: 'absolute',
                right: 48,
                top: 64,
              }}
              onClick={split}
              startIcon={<CallSplitIcon />}
            >
              Split PDF
            </AwaitButton>
          )}
        </DialogTitle>
        <DialogContent>
          {!pdfFile ? (
            <Box {...getRootProps()} sx={{ cursor: 'pointer' }} onClick={openDropzone}>
              <input {...getInputProps()} />
              <Paper
                elevation={isDragActive ? 6 : 1}
                sx={{
                  p: 2,
                  ml: 4,
                  bgcolor: 'background.paper',
                  color: 'info.main',
                  borderColor: 'info.main',
                  border: 1,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'background.default',
                    boxShadow: 3,
                  },
                }}
              >
                <Typography variant="body2" textAlign="center">
                  Click to Upload or Drag and Drop
                </Typography>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ width: '100%', background: '#444' }}>
              <Document file={blobUrl} onLoadSuccess={onDocumentLoadSuccess}>
                {renderPages()}
              </Document>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileInputChange} />
    </>
  );
}
